import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, Smile, Frown, Meh, Users, Target,
  Award, Star, MessageSquare, AlertTriangle, Sparkles, Crown,
  Medal, Trophy, ThumbsUp, BarChart3, PieChart, Filter, Send, ArrowLeft,
  Download, FileSpreadsheet, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import AppHeader from '@/components/shared/AppHeader';
import CampaignComposer from '@/components/admin/CampaignComposer';
import * as XLSX from 'xlsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SentimentBadge = ({ sentiment, size = 'md' }) => {
  const config = {
    positive: { icon: Smile, bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
    negative: { icon: Frown, bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
    neutral: { icon: Meh, bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' }
  };
  const cfg = config[sentiment] || config.neutral;
  const Icon = cfg.icon;
  const sizeClass = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
      <Icon className={sizeClass} />
      <span className="text-xs font-medium capitalize">{sentiment}</span>
    </div>
  );
};

const CustomerIntelligenceDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [sentimentData, setSentimentData] = useState(null);
  const [customerClusters, setCustomerClusters] = useState(null);
  const [companyLeaderboard, setCompanyLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sentiment');
  
  // Appreciation dialog
  const [appreciateDialogOpen, setAppreciateDialogOpen] = useState(false);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [appreciationForm, setAppreciationForm] = useState({
    type: 'performance',
    message: '',
    badge: 'star'
  });

  // Campaign composer state
  const [campaignComposerOpen, setCampaignComposerOpen] = useState(false);
  const [campaignChannel, setCampaignChannel] = useState('email'); // 'email' or 'whatsapp'
  const [campaignSegment, setCampaignSegment] = useState('');
  const [campaignCustomers, setCampaignCustomers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [sentimentRes, clustersRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/analytics/sentiment-dashboard`, { headers }),
        axios.get(`${API}/analytics/customer-clusters`, { headers }),
        axios.get(`${API}/leaderboard/company`, { headers })
      ]);
      
      setSentimentData(sentimentRes.data);
      setCustomerClusters(clustersRes.data);
      setCompanyLeaderboard(leaderboardRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const openAppreciateDialog = (therapist) => {
    setSelectedTherapist(therapist);
    setAppreciationForm({ type: 'performance', message: '', badge: 'star' });
    setAppreciateDialogOpen(true);
  };

  const submitAppreciation = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/appreciate-therapist`, {
        therapist_id: selectedTherapist.therapist_id,
        ...appreciationForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Appreciated ${selectedTherapist.name}!`);
      setAppreciateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to appreciate therapist');
    }
  };

  // Open campaign composer for a segment
  const openCampaignComposer = (channel, segment, customers) => {
    setCampaignChannel(channel);
    setCampaignSegment(segment);
    setCampaignCustomers(customers || []);
    setCampaignComposerOpen(true);
  };

  // Get customers for marketing segment
  const getCustomersForMarketingSegment = (segmentType) => {
    if (!customerClusters?.marketing_segments) return [];
    
    switch (segmentType) {
      case 'premium':
        return customerClusters.marketing_segments.target_for_premium || [];
      case 'retention':
        return customerClusters.marketing_segments.target_for_retention || [];
      case 'upsell':
        return customerClusters.marketing_segments.target_for_upsell || [];
      default:
        return [];
    }
  };

  // Get customers from cluster
  const getCustomersFromCluster = (clusterName) => {
    if (!customerClusters?.clusters) return [];
    return customerClusters.clusters[clusterName] || [];
  };

  // Export customer intelligence data to Excel with tags
  const exportToExcel = () => {
    if (!customerClusters) {
      toast.error('No data to export');
      return;
    }

    const allCustomers = [];
    
    // VIP Customers
    customerClusters.clusters?.vip?.forEach(c => {
      allCustomers.push({
        'Customer Name': c.name,
        'Phone': c.phone,
        'Total Visits': c.total_visits,
        'Avg Rating': c.average_rating || 'N/A',
        'Segment': 'VIP',
        'Tag': 'VIP',
        'Marketing Action': 'Target for premium packages',
        'Priority': 'High',
        'Therapist Positive': c.therapist_positive || 0,
        'Therapist Negative': c.therapist_negative || 0,
        'Reason': c.reason || 'High value customer'
      });
    });

    // Loyal Customers
    customerClusters.clusters?.loyal?.forEach(c => {
      allCustomers.push({
        'Customer Name': c.name,
        'Phone': c.phone,
        'Total Visits': c.total_visits,
        'Avg Rating': c.average_rating || 'N/A',
        'Segment': 'Loyal',
        'Tag': 'Loyal',
        'Marketing Action': 'Loyalty rewards program',
        'Priority': 'Medium',
        'Therapist Positive': c.therapist_positive || 0,
        'Therapist Negative': c.therapist_negative || 0,
        'Reason': c.reason || 'Regular customer'
      });
    });

    // At-Risk Customers
    customerClusters.clusters?.at_risk?.forEach(c => {
      allCustomers.push({
        'Customer Name': c.name,
        'Phone': c.phone,
        'Total Visits': c.total_visits,
        'Avg Rating': c.average_rating || 'N/A',
        'Segment': 'At Risk',
        'Tag': 'At Risk',
        'Marketing Action': 'Win-back campaign',
        'Priority': 'High',
        'Therapist Positive': c.therapist_positive || 0,
        'Therapist Negative': c.therapist_negative || 0,
        'Reason': c.reason || 'Needs attention'
      });
    });

    // Problematic Customers
    customerClusters.clusters?.problematic?.forEach(c => {
      allCustomers.push({
        'Customer Name': c.name,
        'Phone': c.phone,
        'Total Visits': c.total_visits,
        'Avg Rating': c.average_rating || 'N/A',
        'Segment': 'Problematic',
        'Tag': 'Problematic',
        'Marketing Action': 'Handle with care',
        'Priority': 'Low',
        'Therapist Positive': c.therapist_positive || 0,
        'Therapist Negative': c.therapist_negative || 0,
        'Reason': c.reason || 'Flagged by therapists'
      });
    });

    // New Customers
    customerClusters.clusters?.new?.forEach(c => {
      allCustomers.push({
        'Customer Name': c.name,
        'Phone': c.phone,
        'Total Visits': c.total_visits,
        'Avg Rating': c.average_rating || 'N/A',
        'Segment': 'New',
        'Tag': 'New',
        'Marketing Action': 'Welcome campaign',
        'Priority': 'Medium',
        'Therapist Positive': c.therapist_positive || 0,
        'Therapist Negative': c.therapist_negative || 0,
        'Reason': c.reason || 'New customer'
      });
    });

    // Create workbook with multiple sheets
    const wb = XLSX.utils.book_new();
    
    // Main sheet with all customers
    const wsAll = XLSX.utils.json_to_sheet(allCustomers);
    XLSX.utils.book_append_sheet(wb, wsAll, 'All Customers');

    // Summary sheet
    const summaryData = [
      { 'Segment': 'VIP', 'Count': customerClusters.summary?.vip_count || 0, 'Description': 'High value, loved by both', 'Action': 'Premium packages' },
      { 'Segment': 'Loyal', 'Count': customerClusters.summary?.loyal_count || 0, 'Description': 'Regular customers', 'Action': 'Loyalty rewards' },
      { 'Segment': 'At Risk', 'Count': customerClusters.summary?.at_risk_count || 0, 'Description': 'Mixed feedback', 'Action': 'Retention campaign' },
      { 'Segment': 'Problematic', 'Count': customerClusters.summary?.problematic_count || 0, 'Description': 'Flagged by therapists', 'Action': 'Handle with care' },
      { 'Segment': 'New', 'Count': customerClusters.summary?.new_count || 0, 'Description': 'New customers', 'Action': 'Welcome campaign' },
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Marketing Segments sheet
    const marketingData = [
      { 'Campaign': 'Premium Upsell', 'Target': 'VIP Customers', 'Count': customerClusters.marketing_segments?.target_for_premium?.length || 0, 'Description': 'Target VIP for premium packages' },
      { 'Campaign': 'Retention Campaign', 'Target': 'At-Risk Customers', 'Count': customerClusters.marketing_segments?.target_for_retention?.length || 0, 'Description': 'Win back at-risk customers' },
      { 'Campaign': 'Loyalty Rewards', 'Target': 'Loyal Customers', 'Count': customerClusters.marketing_segments?.target_for_upsell?.length || 0, 'Description': 'Reward loyal customers' },
    ];
    const wsMarketing = XLSX.utils.json_to_sheet(marketingData);
    XLSX.utils.book_append_sheet(wb, wsMarketing, 'Marketing Segments');

    // Generate filename with date
    const today = new Date().toISOString().split('T')[0];
    const filename = `Customer_Intelligence_Report_${today}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
    toast.success('Report downloaded successfully!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader title="Customer Intelligence" showBack onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center text-muted-foreground">
          Loading analytics...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="Customer Intelligence" showBack onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Tab Navigation */}
        <div className="flex justify-between items-center border-b pb-2">
          <div className="flex gap-2">
            <Button 
              variant={activeTab === 'sentiment' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('sentiment')}
              data-testid="sentiment-tab"
            >
              <BarChart3 className="w-4 h-4 mr-2" /> Sentiment Analysis
            </Button>
            <Button 
              variant={activeTab === 'customers' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('customers')}
              data-testid="clusters-tab"
            >
              <Users className="w-4 h-4 mr-2" /> Customer Clusters
            </Button>
            <Button 
              variant={activeTab === 'leaderboard' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('leaderboard')}
              data-testid="leaderboard-tab"
            >
              <Trophy className="w-4 h-4 mr-2" /> Leaderboard
            </Button>
          </div>
          
          {/* Export Button */}
          <Button 
            variant="outline" 
            onClick={exportToExcel}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="export-customer-intelligence-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

      {/* Sentiment Analysis Tab */}
      {activeTab === 'sentiment' && sentimentData && (
        <div className="space-y-6">
          {/* Sentiment Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="glass">
              <CardHeader className="pb-2">
                <CardDescription>Total Feedback Analyzed</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{sentimentData.analyzed_feedback}</p>
              </CardContent>
            </Card>
            
            <Card className="glass bg-green-50/50">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1">
                  <Smile className="w-4 h-4 text-green-600" /> Positive
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600">
                  {sentimentData.sentiment_percentages?.positive || 0}%
                </p>
                <p className="text-xs text-muted-foreground">
                  {sentimentData.sentiment_breakdown?.positive || 0} reviews
                </p>
              </CardContent>
            </Card>
            
            <Card className="glass bg-gray-50/50">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1">
                  <Meh className="w-4 h-4 text-gray-600" /> Neutral
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-600">
                  {sentimentData.sentiment_percentages?.neutral || 0}%
                </p>
              </CardContent>
            </Card>
            
            <Card className="glass bg-red-50/50">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1">
                  <Frown className="w-4 h-4 text-red-600" /> Negative
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-red-600">
                  {sentimentData.sentiment_percentages?.negative || 0}%
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Overall Sentiment Indicator */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg font-serif">Overall Sentiment Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                  sentimentData.overall_sentiment === 'positive' ? 'bg-green-100' :
                  sentimentData.overall_sentiment === 'negative' ? 'bg-red-100' : 'bg-gray-100'
                }`}>
                  {sentimentData.overall_sentiment === 'positive' ? (
                    <Smile className="w-10 h-10 text-green-600" />
                  ) : sentimentData.overall_sentiment === 'negative' ? (
                    <Frown className="w-10 h-10 text-red-600" />
                  ) : (
                    <Meh className="w-10 h-10 text-gray-600" />
                  )}
                </div>
                <div>
                  <p className="text-2xl font-bold capitalize">{sentimentData.overall_sentiment}</p>
                  <p className="text-sm text-muted-foreground">
                    Polarity Score: {sentimentData.average_polarity} (-1 to +1)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trending Words */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg font-serif flex items-center gap-2">
                  <ThumbsUp className="w-5 h-5 text-green-600" />
                  Positive Keywords
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {sentimentData.trending_positive_words?.map((word, idx) => (
                    <Badge key={idx} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {word.word} ({word.count})
                    </Badge>
                  ))}
                  {(!sentimentData.trending_positive_words || sentimentData.trending_positive_words.length === 0) && (
                    <p className="text-muted-foreground text-sm">No data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg font-serif flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Improvement Keywords
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {sentimentData.trending_negative_words?.map((word, idx) => (
                    <Badge key={idx} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {word.word} ({word.count})
                    </Badge>
                  ))}
                  {(!sentimentData.trending_negative_words || sentimentData.trending_negative_words.length === 0) && (
                    <p className="text-muted-foreground text-sm">No data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Therapist Sentiment Breakdown */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg font-serif">Therapist Sentiment Performance</CardTitle>
            </CardHeader>
            <CardContent>
              {sentimentData.therapist_sentiments?.length > 0 ? (
                <div className="space-y-3">
                  {sentimentData.therapist_sentiments.map((t, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          {idx === 0 ? <Crown className="w-5 h-5 text-amber-500" /> :
                           <span className="font-bold text-muted-foreground">{idx + 1}</span>}
                        </div>
                        <div>
                          <p className="font-medium">{t.name}</p>
                          <p className="text-xs text-muted-foreground">{t.total} reviews</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">{t.positivity_rate}%</p>
                          <p className="text-xs text-muted-foreground">Positivity Rate</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => openAppreciateDialog({
                          therapist_id: t.therapist_id,
                          name: t.name
                        })}>
                          <Award className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">No therapist sentiment data yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Customer Clusters Tab */}
      {activeTab === 'customers' && customerClusters && (
        <div className="space-y-6">
          {/* Cluster Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="glass bg-purple-50/50 border-purple-200">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1">
                  <Crown className="w-4 h-4 text-purple-600" /> VIP
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-purple-600">{customerClusters.summary?.vip_count || 0}</p>
              </CardContent>
            </Card>
            
            <Card className="glass bg-blue-50/50 border-blue-200">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-blue-600" /> Loyal
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-blue-600">{customerClusters.summary?.loyal_count || 0}</p>
              </CardContent>
            </Card>
            
            <Card className="glass bg-amber-50/50 border-amber-200">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4 text-amber-600" /> At Risk
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-amber-600">{customerClusters.summary?.at_risk_count || 0}</p>
              </CardContent>
            </Card>
            
            <Card className="glass bg-red-50/50 border-red-200">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1">
                  <Frown className="w-4 h-4 text-red-600" /> Problematic
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-red-600">{customerClusters.summary?.problematic_count || 0}</p>
              </CardContent>
            </Card>
            
            <Card className="glass bg-green-50/50 border-green-200">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1">
                  <Sparkles className="w-4 h-4 text-green-600" /> New
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600">{customerClusters.summary?.new_count || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Cluster Details */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* VIP Customers */}
            <Card className="glass border-purple-200">
              <CardHeader className="bg-purple-50/50">
                <CardTitle className="text-lg font-serif flex items-center gap-2">
                  <Crown className="w-5 h-5 text-purple-600" />
                  VIP Customers
                </CardTitle>
                <CardDescription>High value, loved by therapists & positive reviewers</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {customerClusters.clusters?.vip?.length > 0 ? (
                  <div className="space-y-2">
                    {customerClusters.clusters.vip.slice(0, 5).map((c, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-purple-50/50 rounded">
                        <div>
                          <p className="font-medium">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.phone}</p>
                        </div>
                        <Badge className="bg-purple-100 text-purple-700">{c.total_visits} visits</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-4 text-muted-foreground">No VIP customers yet</p>
                )}
              </CardContent>
            </Card>

            {/* Problematic Customers */}
            <Card className="glass border-red-200">
              <CardHeader className="bg-red-50/50">
                <CardTitle className="text-lg font-serif flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Problematic Customers
                </CardTitle>
                <CardDescription>Flagged by therapists - handle with care</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {customerClusters.clusters?.problematic?.length > 0 ? (
                  <div className="space-y-2">
                    {customerClusters.clusters.problematic.slice(0, 5).map((c, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-red-50/50 rounded">
                        <div>
                          <p className="font-medium">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.reason}</p>
                        </div>
                        <Badge variant="destructive">{c.therapist_negative} flags</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-4 text-muted-foreground">No problematic customers</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Marketing Segments */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg font-serif flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Marketing Segments
              </CardTitle>
              <CardDescription>Targeted customer groups for campaigns - Click to send messages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-purple-50/50 rounded-lg border border-purple-200">
                  <p className="font-medium text-purple-700 mb-2">Premium Upsell</p>
                  <p className="text-sm text-muted-foreground mb-2">Target VIP for premium packages</p>
                  <p className="text-2xl font-bold text-purple-600 mb-3">
                    {customerClusters.marketing_segments?.target_for_premium?.length || 0}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1 text-purple-700 border-purple-300 hover:bg-purple-100" onClick={() => openCampaignComposer('email', 'vip', getCustomersForMarketingSegment('premium'))} data-testid="premium-email-btn">
                      <Mail className="w-3 h-3 mr-1" /> Email
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-green-700 border-green-300 hover:bg-green-100" onClick={() => openCampaignComposer('whatsapp', 'vip', getCustomersForMarketingSegment('premium'))} data-testid="premium-whatsapp-btn">
                      <MessageSquare className="w-3 h-3 mr-1" /> WhatsApp
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-amber-50/50 rounded-lg border border-amber-200">
                  <p className="font-medium text-amber-700 mb-2">Retention Campaign</p>
                  <p className="text-sm text-muted-foreground mb-2">Win back at-risk customers</p>
                  <p className="text-2xl font-bold text-amber-600 mb-3">
                    {customerClusters.marketing_segments?.target_for_retention?.length || 0}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1 text-amber-700 border-amber-300 hover:bg-amber-100" onClick={() => openCampaignComposer('email', 'at_risk', getCustomersForMarketingSegment('retention'))} data-testid="retention-email-btn">
                      <Mail className="w-3 h-3 mr-1" /> Email
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-green-700 border-green-300 hover:bg-green-100" onClick={() => openCampaignComposer('whatsapp', 'at_risk', getCustomersForMarketingSegment('retention'))} data-testid="retention-whatsapp-btn">
                      <MessageSquare className="w-3 h-3 mr-1" /> WhatsApp
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-blue-50/50 rounded-lg border border-blue-200">
                  <p className="font-medium text-blue-700 mb-2">Loyalty Rewards</p>
                  <p className="text-sm text-muted-foreground mb-2">Reward loyal customers</p>
                  <p className="text-2xl font-bold text-blue-600 mb-3">
                    {customerClusters.marketing_segments?.target_for_upsell?.length || 0}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1 text-blue-700 border-blue-300 hover:bg-blue-100" onClick={() => openCampaignComposer('email', 'loyal', getCustomersForMarketingSegment('upsell'))} data-testid="loyalty-email-btn">
                      <Mail className="w-3 h-3 mr-1" /> Email
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-green-700 border-green-300 hover:bg-green-100" onClick={() => openCampaignComposer('whatsapp', 'loyal', getCustomersForMarketingSegment('upsell'))} data-testid="loyalty-whatsapp-btn">
                      <MessageSquare className="w-3 h-3 mr-1" /> WhatsApp
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && companyLeaderboard && (
        <div className="space-y-6">
          {/* Champion Card */}
          {companyLeaderboard.champion && (
            <Card className="glass bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300">
              <CardContent className="py-6">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                    <Trophy className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-600 font-medium">COMPANY CHAMPION</p>
                    <p className="text-2xl font-serif font-bold">{companyLeaderboard.champion.name}</p>
                    <p className="text-muted-foreground">{companyLeaderboard.champion.property}</p>
                    <div className="flex gap-4 mt-2">
                      <Badge className="bg-amber-100 text-amber-700">
                        Score: {companyLeaderboard.champion.score}
                      </Badge>
                      <Badge variant="outline">
                        {companyLeaderboard.champion.total_services} services
                      </Badge>
                      <Badge variant="outline">
                        {companyLeaderboard.champion.average_rating}★ rating
                      </Badge>
                    </div>
                  </div>
                  <div className="ml-auto">
                    <Button onClick={() => openAppreciateDialog(companyLeaderboard.champion)}>
                      <Award className="w-4 h-4 mr-2" /> Appreciate
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Full Leaderboard */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg font-serif">Company Leaderboard</CardTitle>
              <CardDescription>Top performers across all spas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {companyLeaderboard.leaderboard?.map((t, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      idx === 0 ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200' :
                      idx === 1 ? 'bg-gray-50 border border-gray-200' :
                      idx === 2 ? 'bg-orange-50/50 border border-orange-200' :
                      'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        idx === 0 ? 'bg-amber-400 text-white' :
                        idx === 1 ? 'bg-gray-400 text-white' :
                        idx === 2 ? 'bg-orange-400 text-white' :
                        'bg-muted'
                      }`}>
                        {idx === 0 ? <Trophy className="w-5 h-5" /> :
                         idx === 1 ? <Medal className="w-5 h-5" /> :
                         idx === 2 ? <Medal className="w-5 h-5" /> :
                         <span className="font-bold">{t.rank}</span>}
                      </div>
                      <div>
                        <p className="font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.property}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-lg font-bold">{t.score}</p>
                        <p className="text-xs text-muted-foreground">Score</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium">{t.total_services}</p>
                        <p className="text-xs text-muted-foreground">Services</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium flex items-center gap-1">
                          {t.average_rating} <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        </p>
                        <p className="text-xs text-muted-foreground">Rating</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium">{t.appreciation_count}</p>
                        <p className="text-xs text-muted-foreground">Awards</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => openAppreciateDialog(t)}>
                        <Award className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {(!companyLeaderboard.leaderboard || companyLeaderboard.leaderboard.length === 0) && (
                  <p className="text-center py-8 text-muted-foreground">No leaderboard data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Appreciation Dialog */}
      <Dialog open={appreciateDialogOpen} onOpenChange={setAppreciateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Appreciate Therapist
            </DialogTitle>
          </DialogHeader>
          
          {selectedTherapist && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">{selectedTherapist.name}</p>
              </div>
              
              <div>
                <Label>Type of Appreciation</Label>
                <Select 
                  value={appreciationForm.type} 
                  onValueChange={(v) => setAppreciationForm(prev => ({...prev, type: v}))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="performance">Outstanding Performance</SelectItem>
                    <SelectItem value="customer_service">Excellent Customer Service</SelectItem>
                    <SelectItem value="team_player">Team Player</SelectItem>
                    <SelectItem value="consistency">Consistent Quality</SelectItem>
                    <SelectItem value="improvement">Most Improved</SelectItem>
                    <SelectItem value="special">Special Recognition</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Award Badge</Label>
                <Select 
                  value={appreciationForm.badge} 
                  onValueChange={(v) => setAppreciationForm(prev => ({...prev, badge: v}))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="star">⭐ Star</SelectItem>
                    <SelectItem value="medal">🏅 Medal</SelectItem>
                    <SelectItem value="trophy">🏆 Trophy</SelectItem>
                    <SelectItem value="crown">👑 Crown</SelectItem>
                    <SelectItem value="heart">❤️ Heart</SelectItem>
                    <SelectItem value="sparkle">✨ Sparkle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Personal Message (Optional)</Label>
                <Textarea
                  value={appreciationForm.message}
                  onChange={(e) => setAppreciationForm(prev => ({...prev, message: e.target.value}))}
                  placeholder="Write a personal appreciation message..."
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setAppreciateDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={submitAppreciation} className="flex-1">
                  <Send className="w-4 h-4 mr-2" /> Send Appreciation
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Campaign Composer Dialog */}
      <CampaignComposer
        isOpen={campaignComposerOpen}
        onClose={() => setCampaignComposerOpen(false)}
        segment={campaignSegment}
        customers={campaignCustomers}
        channel={campaignChannel}
      />
      </div>
    </div>
  );
};

export default CustomerIntelligenceDashboard;
